import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { affectsStock } from '../config/tranTypes';

// ─── Defaults ─────────────────────────────────────────────────────────────────
// Mirror current hardcoded behaviour so enabling the module without saving
// changes nothing.
export const DEFAULT_INVENTORY_MOVEMENT_CONFIG = {
  inMode: 'invoice',   // 'invoice' → PI/NPI/IPI move stock in, GRN does not
                       // 'grn'     → GRN moves stock in, PI/NPI/IPI do not
  outMode: 'invoice',  // 'invoice' → SI/NSI move stock out, DC does not
                       // 'challan' → DC moves stock out, SI/NSI do not
};

// Doc types that carry an "in" direction and are user-configurable
const IN_DOC_TYPES = new Set(['GRN', 'PI', 'NPI', 'IPI']);
// Doc types that carry an "out" direction and are user-configurable
const OUT_DOC_TYPES = new Set(['DC', 'SI', 'NSI']);

// ─── Firestore I/O ───────────────────────────────────────────────────────────
let _cache = {}; // { [companyId]: { config, loadedAt } }
const CACHE_TTL_MS = 60 * 1000;

export async function loadInventoryMovementConfig(companyId) {
  if (!companyId) return { ...DEFAULT_INVENTORY_MOVEMENT_CONFIG };
  const cached = _cache[companyId];
  if (cached && Date.now() - cached.loadedAt < CACHE_TTL_MS) return cached.config;
  try {
    const ref = doc(db, 'companies', companyId, 'settings', 'inventoryMovement');
    const snap = await getDoc(ref);
    const saved = snap.exists() ? snap.data() : {};
    const config = {
      ...DEFAULT_INVENTORY_MOVEMENT_CONFIG,
      ...saved,
    };
    _cache[companyId] = { config, loadedAt: Date.now() };
    return config;
  } catch (e) {
    console.error('Error loading inventory movement config:', e);
    return { ...DEFAULT_INVENTORY_MOVEMENT_CONFIG };
  }
}

export async function saveInventoryMovementConfig(companyId, config, userId) {
  if (!companyId) throw new Error('companyId is required');
  const ref = doc(db, 'companies', companyId, 'settings', 'inventoryMovement');
  const payload = {
    inMode: config.inMode === 'grn' ? 'grn' : 'invoice',
    outMode: config.outMode === 'challan' ? 'challan' : 'invoice',
    updatedAt: new Date().toISOString(),
    updatedBy: userId || null,
  };
  await setDoc(ref, payload);
  _cache[companyId] = { config: { ...DEFAULT_INVENTORY_MOVEMENT_CONFIG, ...payload }, loadedAt: Date.now() };
  return payload;
}

// ─── Decision function ───────────────────────────────────────────────────────
/**
 * Decide whether a transaction should post to stockFlow, given the company's
 * inventory movement config.
 *
 * Third arg accepts EITHER the parent's tran-type code (string like 'DC') for
 * precise checking, OR a boolean (legacy: any parent = "linked"). The
 * parent-type form is preferred because the SI/NSI skip is specifically about
 * "linked to a DC that already posted stock" — being linked to a SO or QT
 * shouldn't suppress the SI from posting stock.
 */
export function shouldPostStock(tranTypeCode, parentInfo, config) {
  const cfg = { ...DEFAULT_INVENTORY_MOVEMENT_CONFIG, ...(config || {}) };
  // Backward-compat: callers used to pass a boolean. If so, treat it as
  // hasAnyParent and assume the parent is a DC for OUT pairs (the legacy
  // worst-case behavior).
  const parentTranTypeCode = typeof parentInfo === 'string' ? parentInfo : null;
  const hasAnyParent = typeof parentInfo === 'boolean' ? parentInfo : !!parentTranTypeCode;
  const parentIsStockPosting = parentTranTypeCode === 'DC' || parentTranTypeCode === 'GRN';

  // Configurable IN pair
  if (IN_DOC_TYPES.has(tranTypeCode)) {
    if (tranTypeCode === 'GRN') return cfg.inMode === 'grn';
    // PI / NPI / IPI: post stock when inMode === 'invoice' (regardless of GRN parent —
    // in 'invoice' mode the GRN does not move stock, so the invoice must).
    return cfg.inMode === 'invoice';
  }

  // Configurable OUT pair
  if (OUT_DOC_TYPES.has(tranTypeCode)) {
    if (tranTypeCode === 'DC') return cfg.outMode === 'challan';
    // SI / NSI: post stock when outMode === 'invoice' (regardless of DC parent —
    // in 'invoice' mode the DC does not move stock, so the invoice must).
    return cfg.outMode === 'invoice';
  }

  // Everything else — returns, transfers, adjustments, POS, production, etc.
  return affectsStock(tranTypeCode, parentIsStockPosting || (hasAnyParent && !parentTranTypeCode));
}

// ─── Metadata for the settings UI ────────────────────────────────────────────
export const INVENTORY_MOVEMENT_IN_OPTIONS = [
  { value: 'invoice', label: 'On Purchase Invoice', description: 'Stock enters inventory when the Purchase Invoice is posted. GRN becomes a receipt note only.' },
  { value: 'grn', label: 'On GRN (Goods Receipt)', description: 'Stock enters inventory on GRN posting. Purchase Invoice posts only accounting, not stock.' },
];

export const INVENTORY_MOVEMENT_OUT_OPTIONS = [
  { value: 'invoice', label: 'On Sale Invoice', description: 'Stock leaves inventory when the Sale Invoice is posted. Delivery Challan becomes a dispatch note only.' },
  { value: 'challan', label: 'On Delivery Challan', description: 'Stock leaves inventory when the DC is posted. Sale Invoice posts only accounting, not stock.' },
];

// Docs that always move stock — shown as reference, not configurable
export const ALWAYS_IN_DOCS = [
  { code: 'OB', label: 'Opening Balance' },
  { code: 'AS', label: 'Add Stock (Adjustment)' },
  { code: 'SR', label: 'Sale Return (reverses sale)' },
  { code: 'NSR', label: 'Non-Tax Sale Return (reverses sale)' },
  { code: 'ST', label: 'Stock Transfer (inward side)' },
  { code: 'BT', label: 'Branch Transfer (inward side)' },
  { code: 'PO', label: 'Production Order (finished goods)' },
];

export const ALWAYS_OUT_DOCS = [
  { code: 'RS', label: 'Reduce Stock (Adjustment)' },
  { code: 'PS', label: 'POS Sale' },
  { code: 'PR', label: 'Purchase Return (reverses purchase)' },
  { code: 'NPR', label: 'Non-Tax Purchase Return (reverses purchase)' },
  { code: 'ST', label: 'Stock Transfer (outward side)' },
  { code: 'BT', label: 'Branch Transfer (outward side)' },
  { code: 'PO', label: 'Production Order (components consumed)' },
];
